import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { MoonIcon, SunIcon, ThermometerIcon } from 'lucide-react';
export function MechanismSection() {
  const ref = useScrollReveal();
  return <section ref={ref} className="w-full section-padding" style={{
    backgroundColor: '#1a2744'
  }} aria-label="The Circadian Anchor mechanism" data-id="element-200">
      {/* Star decorations */}
      <div className="absolute pointer-events-none" aria-hidden="true" data-id="element-201">
        <span className="text-yellow-200 opacity-10 text-xs" data-id="element-202">✦</span>
      </div>

      <div className="max-w-3xl mx-auto text-center relative" data-id="element-203">
        {/* Label */}
        <div className="scroll-reveal mb-8" data-id="element-204">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1.5 rounded-full border" style={{
          color: '#c9a96e',
          borderColor: 'rgba(201,169,110,0.4)',
          backgroundColor: 'rgba(201,169,110,0.08)',
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-205">
            The Unique Mechanism
          </span>
        </div>

        {/* Heading */}
        <div className="scroll-reveal scroll-reveal-delay-1 mb-6" data-id="element-206">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-white" style={{
          fontFamily: "'Playfair Display', serif"
        }} data-id="element-207">
            Introducing:{' '}
            <span className="italic" style={{
            color: '#c9a96e'
          }} data-id="element-208">
              The Circadian Anchor.
            </span>
          </h2>
        </div>

        <div className="scroll-reveal scroll-reveal-delay-2 gold-divider mb-8" data-id="element-209" />

        {/* Body */}
        <div className="scroll-reveal scroll-reveal-delay-3 mb-12" data-id="element-210">
          <p className="text-lg md:text-xl leading-relaxed" style={{
          color: '#d4cfc8',
          fontFamily: "'Inter', sans-serif"
        }} data-id="element-211">
            Instead of fighting your toddler's willpower, we{' '}
            <strong className="text-white" data-id="element-212">manipulate their biology.</strong> By
            aligning light exposure, temperature drops, and protein-timing, we
            "lock" their internal clock into a 12-hour deep-sleep window.
          </p>
        </div>

        {/* Three pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-213">
          <div className="scroll-reveal scroll-reveal-delay-2 rounded-2xl p-6 text-center" style={{
          backgroundColor: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(201,169,110,0.2)'
        }} data-id="element-214">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{
            backgroundColor: 'rgba(201,169,110,0.15)'
          }} data-id="element-215">
              <SunIcon className="w-6 h-6" style={{
              color: '#c9a96e'
            }} aria-hidden="true" data-id="element-216" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-217">
              Light Exposure
            </h3>
            <p className="text-sm leading-relaxed" style={{
            color: '#a09890',
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-218">
              Strategic morning light and evening darkness to trigger natural
              melatonin production at exactly the right time.
            </p>
          </div>

          <div className="scroll-reveal scroll-reveal-delay-3 rounded-2xl p-6 text-center" style={{
          backgroundColor: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(201,169,110,0.2)'
        }} data-id="element-219">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{
            backgroundColor: 'rgba(201,169,110,0.15)'
          }} data-id="element-220">
              <ThermometerIcon className="w-6 h-6" style={{
              color: '#c9a96e'
            }} aria-hidden="true" data-id="element-221" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-222">
              Temperature Drop
            </h3>
            <p className="text-sm leading-relaxed" style={{
            color: '#a09890',
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-223">
              A precise room temperature protocol that signals the brain to
              begin its natural sleep descent.
            </p>
          </div>

          <div className="scroll-reveal scroll-reveal-delay-4 rounded-2xl p-6 text-center" style={{
          backgroundColor: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(201,169,110,0.2)'
        }} data-id="element-224">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{
            backgroundColor: 'rgba(201,169,110,0.15)'
          }} data-id="element-225">
              <MoonIcon className="w-6 h-6" style={{
              color: '#c9a96e'
            }} aria-hidden="true" data-id="element-226" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-227">
              Protein Timing
            </h3>
            <p className="text-sm leading-relaxed" style={{
            color: '#a09890',
            fontFamily: "'Inter', sans-serif"
          }} data-id="element-228">
              A nutritional timing protocol that ends early-morning hunger
              wake-ups and stabilizes overnight sleep chemistry.
            </p>
          </div>
        </div>

        {/* Key claim */}
        <div className="scroll-reveal scroll-reveal-delay-5 rounded-2xl p-6 md:p-8" style={{
        backgroundColor: 'rgba(201,169,110,0.1)',
        border: '1px solid rgba(201,169,110,0.3)'
      }} data-id="element-229">
          <p className="text-xl md:text-2xl font-bold italic" style={{
          fontFamily: "'Playfair Display', serif",
          color: '#c9a96e'
        }} data-id="element-230">
            "We don't use Cry It Out. We use biology to make their brain crave
            sleep at exactly 7:30 PM."
          </p>
        </div>
      </div>
    </section>;
}